import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';
import { Briefcase, Plus, Search, ChevronRight, Building2, Users, Loader2 } from 'lucide-react';

const STATUSES = ['Planned', 'Active', 'On Hold', 'Completed', 'Archived'];
const TYPE_SUGGESTIONS = [
  'Consulting Project', 'Counselling Program', 'Mediation', 'Facilitation / Workshop',
  'Implementation', 'Retainer', 'Design Project', 'Client Project', 'Service Engagement', 'Other',
];
const STATUS_COLORS = {
  Planned: 'bg-blue-100 text-blue-700', Active: 'bg-emerald-100 text-emerald-700',
  'On Hold': 'bg-amber-100 text-amber-700', Completed: 'bg-gray-100 text-gray-700',
  Archived: 'bg-gray-100 text-gray-500',
};
const EMPTY = { name: '', type: '', status: 'Planned', description: '', start_date: '', end_date: '' };

export default function Engagements() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get, post } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [preselect, setPreselect] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchEngagements = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      setEngagements((await get('/engagements', params)) || []);
    } catch (e) {
      console.error('Failed to load engagements', e);
    } finally {
      setLoading(false);
    }
  }, [get, search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(fetchEngagements, 250);
    return () => clearTimeout(t);
  }, [fetchEngagements]);

  // Pre-filled create from another record ("Start Engagement from X").
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setPreselect({
        organization_id: searchParams.get('organization_id') || undefined,
        contact_id: searchParams.get('contact_id') || undefined,
        opportunity_id: searchParams.get('opportunity_id') || undefined,
      });
      const ctxName = searchParams.get('context_name');
      setForm({ ...EMPTY, name: ctxName ? `${ctxName} engagement` : '' });
      setCreateOpen(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Engagement name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...Object.fromEntries(Object.entries(form).filter(([, v]) => v !== '')), ...preselect };
      const created = await post('/engagements', payload);
      toast.success('Engagement created');
      setCreateOpen(false);
      setForm(EMPTY);
      setPreselect({});
      navigate(`/engagements/${created.id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const canCreate = user?.role !== 'executive_viewer';

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="engagements-page">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl flex items-center gap-2">
              <Briefcase className="h-6 w-6" /> Engagements
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your active work, projects and client relationships</p>
          </div>
          {canCreate && (
            <Button className="rounded-sm" onClick={() => { setForm(EMPTY); setPreselect({}); setCreateOpen(true); }} data-testid="create-engagement-btn">
              <Plus className="h-4 w-4 mr-2" /> New Engagement
            </Button>
          )}
        </div>

        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search engagements..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-sm" data-testid="engagements-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] rounded-sm" data-testid="engagements-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[300px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : engagements.length === 0 ? (
          <Card className="rounded-sm border"><CardContent className="text-center py-12">
            <Briefcase className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">No engagements yet</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="engagements-list">
            {engagements.map((e) => (
              <button key={e.id} onClick={() => navigate(`/engagements/${e.id}`)} className="w-full text-left" data-testid={`engagement-row-${e.id}`}>
                <Card className="rounded-sm border hover:border-primary/50 hover:shadow-sm transition-all">
                  <CardContent className="p-4 flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{e.name}</p>
                        <Badge className={`rounded-sm text-xs ${STATUS_COLORS[e.status] || ''}`}>{e.status}</Badge>
                        {e.type && <Badge variant="outline" className="rounded-sm text-xs">{e.type}</Badge>}
                      </div>
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground flex-wrap">
                        {e.organization_name && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{e.organization_name}</span>}
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{e.participant_count} people</span>
                        {e.owner_name && <span>Owner: {e.owner_name}</span>}
                        {e.last_activity_date && <span>Updated {formatDateTime(e.last_activity_date)}</span>}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>New Engagement</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-sm" data-testid="engagement-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="engagement-type-input"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{TYPE_SUGGESTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-sm font-medium">Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm" /></div>
              <div className="space-y-2"><Label className="text-sm font-medium">End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm" /></div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Description / purpose</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="rounded-sm" data-testid="submit-engagement-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
